import { createContext, useCallback, useContext, useEffect, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import set from "lodash/set";
import get from "lodash/get";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";

const Ctx = createContext(null);
export const useAdmin = () => useContext(Ctx);

export const AdminProvider = ({ children, onLogout }) => {
  const [content, setContent] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [unpublished, setUnpublished] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    adminApi.get("/admin/content").then((r) => { setContent(r.data.content); setUnpublished(r.data.has_unpublished); }).catch((e) => toast.error(errMsg(e)));
  }, []);

  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const val = useCallback((path, fallback = "") => get(content, path, fallback) ?? fallback, [content]);
  const update = useCallback((path, value) => {
    setContent((prev) => { const n = cloneDeep(prev); set(n, path, value); return n; });
    setDirty(true);
  }, []);

  const save = async (quiet) => {
    setBusy("save");
    try {
      await adminApi.put("/admin/content", { content });
      setDirty(false); setUnpublished(true);
      if (!quiet) toast.success("Changes saved as draft");
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(""); }
  };

  const publish = async () => {
    if (dirty && !(await save(true))) return;
    setBusy("publish");
    try { await adminApi.post("/admin/publish"); setUnpublished(false); toast.success("Published! Your website is now live with these changes."); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const preview = async () => {
    const w = window.open("about:blank", "_blank");
    if (dirty && !(await save(true))) { w?.close(); return; }
    if (w) w.location.href = "/?preview=1";
  };

  const discard = async () => {
    setBusy("discard");
    try {
      await adminApi.post("/admin/discard");
      const r = await adminApi.get("/admin/content");
      setContent(r.data.content); setDirty(false); setUnpublished(false);
      toast.success("Draft reset to the live website");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <Ctx.Provider value={{ content, val, update, dirty, unpublished, busy, save, publish, preview, discard, onLogout }}>
      {children}
    </Ctx.Provider>
  );
};
